import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardStats } from '../types';
import { dashboardApi, cullsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.uid) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const culls = await cullsApi.getUserCulls(user.uid);
        const data = await dashboardApi.getStats(culls);
        setStats(data);
      } catch (error: any) {
        console.error('Error fetching dashboard stats:', error);
        setError(`Failed to load dashboard data: ${error.message || error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  if (!stats) {
    return <div className="dashboard-error">No data available</div>;
  }

  // Show empty state if no records
  if (stats.total_culls === 0) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p>Overview of your cull records</p>
        </div>

        <div className="dashboard-empty">
          <h2>Welcome to your Dashboard!</h2>
          <p>You haven't added any cull records yet. Start by adding your first record to see analytics and insights here.</p>
          <Link to="/add-cull" className="cta-button">
            Add Your First Record
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Overview of your cull records</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Culls</h3>
          <div className="stat-number">{stats.total_culls}</div>
        </div>
        <div className="stat-card">
          <h3>This Month</h3>
          <div className="stat-number">
            {stats.this_month}
          </div>
        </div>
        <div className="stat-card">
          <h3>Average Weight</h3>
          <div className="stat-text">
            {stats.average_weight ? `${stats.average_weight} kg` : 'N/A'}
          </div>
        </div>
        <div className="stat-card">
          <h3>Top Species</h3>
          <div className="stat-text">
            {Object.keys(stats.species_breakdown).length > 0 
              ? Object.entries(stats.species_breakdown).sort(([,a], [,b]) => b - a)[0][0]
              : 'N/A'}
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-container">
          <h3>Species Breakdown</h3>
          {Object.keys(stats.species_breakdown).length > 0 ? (
            <div className="species-list">
              {Object.entries(stats.species_breakdown)
                .sort(([,a], [,b]) => b - a) // Sort by count descending
                .map(([species, count]) => (
                <div key={species} className="species-item">
                  <span className="species-name">{species}</span>
                  <span className="species-count">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-chart">
              <p>No species data available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;